import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { expensesApi as eapi, formatCents } from '../../../api/expenses.js';

/**
 * Expense report list + create. Admins (expense_admin) see all reports; an
 * employee self-serves their own by passing ?employee=<their employeeId>.
 * The create form takes an explicit employee id so admins can file on behalf.
 */
export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await eapi.listReports();
      setReports(r?.items || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function createReport(e) {
    e.preventDefault();
    if (!employeeId || !title) return;
    setBusy(true);
    setError(null);
    try {
      await eapi.createReport({ employeeId, title });
      setTitle('');
      await load();
    } catch (e2) {
      setError(e2.response?.data?.message || 'Failed to create report');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="expenses-reports-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>My Reports</h1>
          <p className="muted">Create an expense report, add lines, then submit for approval.</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="expenses-reports-error">{error}</div>}

      <form onSubmit={createReport} className="card form-card row-form">
        <label>Employee id
          <input
            data-testid="expenses-report-employee-input"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            placeholder="employee id"
          />
        </label>
        <label>Title
          <input
            data-testid="expenses-report-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. June Client Dinners"
          />
        </label>
        <div className="actions">
          <button className="btn primary" type="submit" data-testid="expenses-report-create" disabled={busy || !employeeId || !title}>Create report</button>
        </div>
      </form>

      <div className="card">
        <h3>Reports</h3>
        <table className="data-table" data-testid="expenses-report-list">
          <thead><tr><th>Title</th><th>Status</th><th>Total</th><th></th></tr></thead>
          <tbody>
            {reports.map((r) => (
              <tr key={r.id} data-testid={`expenses-report-row-${r.id}`}>
                <td><Link to={`/expenses/reports/${r.id}`}>{r.title}</Link></td>
                <td data-testid={`expenses-report-status-${r.id}`}>{r.status}</td>
                <td>{formatCents(r.total_cents, r.currency)}</td>
                <td><Link className="btn ghost" to={`/expenses/reports/${r.id}`}>Open</Link></td>
              </tr>
            ))}
            {!loading && reports.length === 0 && <tr><td colSpan={4} className="muted">No reports yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
